<template>
  <div class="relative mb-6">
    <template v-if="!expired">
      <div class="relative">
        <div
          class="mb-0 leading-tight"
          :class="$style['layout-color']"
        >
          <div
            class="py-0 px-3 quill-class"
            v-html="timelineBody.timeline"
          />
        </div>

        <div>
          <template v-if="reactionList.length > 0 && !timelineConfig.isPublic">
            <XEventFeedback
              v-if="timelineConfig.eventFeedback.enabled"
              :value="eventFeedback"
              :eventFeedback="timelineConfig.eventFeedback"
            />
          </template>

          <template v-if="pinnedReactions.length > 0">
            <div
              v-for="reaction in pinnedReactions"
              :key="reaction.id"
            >
              <div class="mb-4">
                <component
                  :ref="reaction.id"
                  :is="getComponentFromType(reaction.type)"
                  :reaction="reaction"
                  :shareChannels="shareChannels"
                  :shareDescription="shareDescription"
                  :shareMessage="shareMessage"
                  :shareHashtags="shareHashtags"
                  showDateType
                  allowHijack
                  :allowExpressions="allowExpressions"
                  @fieldsUpdated="onFieldsUpdated"
                />
              </div>
            </div>
          </template>

          <template v-for="(question, index) in questions">
            <XQuestionAction
              v-if="question.isActive"
              :key="index"
              :question="question"
            />
          </template>

          <XScratchAndWin
            v-if="timelineConfig.scratchAndWin.enabled"
            :scratchAndWinConfig="timelineConfig.scratchAndWin"
            @addReaction="addReaction"
          />

          <component
            v-for="(actionComponent, index) in actionComponents"
            :key="index"
            :is="actionComponent.component"
            :id="actionComponent.id"
            :name="actionComponent.name"
            :settings="actionComponent.settings"
            :reactionCount="actionComponent.reactionCount"
            @addReaction="addReaction"
          />

          <template v-if="noPinnedReactions.length > 0">
            <div
              v-for="reaction in noPinnedReactions"
              :key="reaction.id"
            >
              <div class="mb-4">
                <component
                  :ref="reaction.id"
                  :is="getComponentFromType(reaction.type)"
                  :reaction="reaction"
                  :shareChannels="shareChannels"
                  :shareDescription="shareDescription"
                  :shareMessage="shareMessage"
                  :shareHashtags="shareHashtags"
                  showDateType
                  allowHijack
                  :allowExpressions="allowExpressions"
                  @fieldsUpdated="onFieldsUpdated"
                />
              </div>
            </div>
          </template>
        </div>

        <XCard
          v-if="reactionList.length <= 0 && !timelineConfig.scratchAndWin.enabled"
          class="py-6 px-4"
        >
          <div
            class="py-2 px-3 quill-class"
            v-html="timelineBody.noReactions"
          />
        </XCard>
      </div>
    </template>

    <XCard
      v-else
      class="py-6 px-4 mt-16"
    >
      <div
        class="py-2 px-3 quill-class"
        v-html="timelineBody.closed"
      />
    </XCard>
  </div>
</template>

<script>
import _ from 'lodash'
import { DateTime } from 'luxon'

import formatDateToRelative from '@common/utils/formatDateToRelative'
import scrollTo from '@plat/util/scroller'

import XEventFeedback from './actions/EventFeedback'
import XScratchAndWin from './actions/ScratchAndWin'
import XRaffle from './actions/Raffle'
import XQuestionAction from './actions/QuestionAction'

import XScratchAndWinReaction from '@plat/views/event/reactions/ScratchAndWinReaction'
import XRaffleReaction from '@plat/views/event/reactions/RaffleReaction'
import XBroadcastReaction from '@plat/views/event/reactions/broadcast/Index'
import XImageReaction from '@plat/views/event/reactions/ImageReaction'
import XGifReaction from '@plat/views/event/reactions/GifReaction'
import XVideoReaction from '@plat/views/event/reactions/VideoReaction'
import XFileReaction from '@plat/views/event/reactions/FileReaction'

export default {
  components: {
    XQuestionAction,
    XEventFeedback,
    XScratchAndWin,
    XRaffle,
    XScratchAndWinReaction,
    XRaffleReaction,
    XBroadcastReaction,
    XImageReaction,
    XGifReaction,
    XVideoReaction,
    XFileReaction
  },

  props: {
    timelineBody: {
      type: Object
    },
    timelineConfig: {
      type: Object
    },
    expired: {
      type: Boolean
    },
    reactions: {
      type: Array
    },
    eventFeedback: {
      type: Object,
      default: null
    },
    questions: {
      type: Array
    },
    actions: {
      type: Array
    }
  },

  data () {
    return {
      reactionsData: _.cloneDeep(this.reactions)
    }
  },

  watch: {
    reactions: function () {
      this.reactionsData = _.cloneDeep(this.reactions)
    }
  },

  computed: {
    actionComponents () {
      return this.actions
        .map(action => {
          let reactions = this.reactionsByAction(action)

          return {
            ...action,
            component: 'X' + _.upperFirst(action.type),
            reactionCount: reactions.length,
            hotAdded: reactions.filter(reaction => reaction.hotAdded).length
          }
        })
        .filter(this.actionAvailable)
    },

    shareChannels () {
      const formattedChannels = _.map(this.timelineConfig.share.channels, (withChannel, type) => ({
        channel: type,
        enabled: withChannel
      }))
      const filteredChannels = _.filter(formattedChannels, 'enabled')
      const mappedChannels = _.map(filteredChannels, 'channel')

      return mappedChannels.length > 0
        ? mappedChannels
        : null
    },

    shareDescription () {
      return this.timelineConfig.share.description || ''
    },

    shareMessage () {
      return this.timelineConfig.share.message || ''
    },

    shareHashtags () {
      return this.timelineConfig.share.hashtags
    },

    allowExpressions () {
      return this.timelineConfig.expressions.enabled
    },

    reactionList () {
      if (this.reactionsData.length === 0) {
        return []
      }

      return this.reactionsData
        .map(reaction => {
          const date = reaction.type === 'broadcast'
            ? DateTime.fromISO(reaction.data.publicationDate)
            : DateTime.fromISO(reaction.createdAt)
          const relativeDate = formatDateToRelative(date)

          return {
            ...reaction,
            relativeDate: this.$tc(
              'common.dates.' + relativeDate.unit,
              relativeDate.diff,
              { unit: relativeDate.diff }
            )
          }
        })
    },

    noPinnedReactions () {
      return this.reactionList
        .filter(reaction => {
          return (this.reactionHasPinnedProperty(reaction) && !reaction.data.pinned) ||
                 !this.reactionHasPinnedProperty(reaction)
        })
    },

    pinnedReactions () {
      return this.reactionList
        .filter(reaction => this.reactionHasPinnedProperty(reaction) && reaction.data.pinned
        )
    }
  },

  methods: {
    reactionHasPinnedProperty (reaction) {
      return reaction && reaction.data && reaction.data.hasOwnProperty('pinned')
    },

    getComponentFromType (reactionType) {
      if (
        reactionType === 'photoOverlay' ||
        reactionType === 'photoChromaKey' ||
        reactionType === 'photoSelfie' ||
        reactionType === 'photoRaw' ||
        reactionType === 'chromaSelfie'
      ) {
        return 'XImageReaction'
      }

      if (
        reactionType === 'videoRaw' ||
        reactionType === 'videoUrl'
      ) {
        return 'XVideoReaction'
      }

      return 'X' + _.upperFirst(reactionType) + 'Reaction'
    },

    reactionsByAction (action) {
      if (action.type === 'raffle') {
        return this.reactionsData.filter(
          reaction => reaction.type === 'raffle' &&
                      action.settings.items.find(item => item.ref === reaction.data.ref) !== undefined
        )
      }

      return this.reactionsData.filter(reaction => reaction.type === action.type)
    },

    actionAvailable (action) {
      if (action.type === 'raffle') {
        return (action.reactionCount - action.hotAdded) < parseInt(action.settings.maxParticipations)
      }

      return true
    },

    addReaction (reaction) {
      this.$emit('reactionAdded')

      this.reactionsData.unshift({
        ...reaction,
        hotAdded: true
      })

      this.scrollToElementPosition(reaction.id)
    },

    scrollToElementPosition (elementRef) {
      return new Promise(resolve => {
        this.$nextTick(() => {
          let element = this.$refs[elementRef][0].$el

          scrollTo(element)

          resolve(element)
        })
      })
    },

    onFieldsUpdated () {
      this.$emit('fieldsUpdated')
    }
  }
}
</script>

<style module>
.line {
  stroke: #c7c7c7;
  stroke-width: 2px;
  stroke-dasharray: 100, 1;
  stroke-linecap: round;
}

.layout-color {
  background-color: #fafafa;
}

.down-icon {
  left: -60px;
  background-color: #fafafa;
  color: #c7c7c7;
}

.footer-image {
  max-width: 120px;
  min-width: 35px;
  max-height: 120px;
  min-height: 35px;
}
</style>
